import React, { useEffect, useRef, useState } from 'react';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Download, Upload, ShieldCheck, Loader2, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * One-click full-system backup + restore. Super-Admin only.
 *
 * Download → /api/pollen/backup/system-snapshot streams a ZIP.
 * Restore → /api/pollen/backup/restore-snapshot wipes every owned
 *           collection and rehydrates it from the uploaded ZIP. Same
 *           ZIP that came out of Download can be uploaded back in to
 *           land exactly at that point in time.
 */
const SystemBackupCard = () => {
  const [downloading, setDownloading] = useState(false);
  const [last, setLast] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const fetchLast = async () => {
    try {
      const res = await fetch(`${API}/pollen/backup/last-snapshot`, { credentials: 'include' });
      if (res.ok) setLast(await res.json());
    } catch {}
  };
  useEffect(() => { fetchLast(); }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/pollen/backup/system-snapshot`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Backup failed — admin role required');
        return;
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const m = /filename="?([^"]+)"?/.exec(disposition);
      const today = new Date().toISOString().slice(0, 10);
      const fname = m?.[1] || `pollendots-backup-${today}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fname}`);
      fetchLast();
    } catch (e) {
      toast.error('Backup failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) { toast.error('Pick a backup ZIP first'); return; }
    if (confirmText !== 'RESTORE') { toast.error('Type RESTORE to confirm'); return; }
    setRestoring(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', restoreFile);
      fd.append('confirm', 'RESTORE');
      const res = await fetch(`${API}/pollen/backup/restore-snapshot`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(body.detail || 'Restore failed');
        return;
      }
      setResult(body);
      const total = Object.values(body.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restored ${total} records from snapshot of ${body.snapshot_taken_at?.slice(0, 19) || '\u2014'}`);
      fetchLast();
    } catch (e) {
      toast.error('Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const closeRestore = () => {
    if (restoring) return;
    setRestoreOpen(false);
    setRestoreFile(null);
    setConfirmText('');
    setResult(null);
  };

  const timeAgo = (iso) => {
    if (!iso) return null;
    try {
      const t = new Date(iso).getTime();
      const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.round(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return `${Math.round(hrs / 24)}d ago`;
    } catch { return null; }
  };

  return (
    <>
      <Card data-testid="system-backup-card" className="border-amber-200/60 bg-gradient-to-br from-amber-50/40 to-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-amber-700" />
            System Backup &amp; Restore
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Download a complete snapshot of every tour, customer, vendor, location, payment and document as a single ZIP. The same ZIP can be uploaded back in to roll the entire system to the exact state it was in at backup time.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <Button
              onClick={handleDownload}
              disabled={downloading}
              data-testid="download-backup-btn"
              className="bg-amber-700 hover:bg-amber-800 text-white"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {downloading ? 'Preparing backup\u2026' : 'Download today\u2019s backup'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setRestoreOpen(true)}
              data-testid="open-restore-btn"
              className="border-amber-700 text-amber-700 hover:bg-amber-50"
            >
              <Upload className="w-4 h-4 mr-2" />
              Restore from backup{'\u2026'}
            </Button>
            {last?.timestamp && (
              <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="last-backup-meta">
                <Clock className="w-3 h-3" />
                Last backup: {timeAgo(last.timestamp)} by {last.user_email || '\u2014'}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={restoreOpen} onOpenChange={(o) => { if (!o) closeRestore(); }}>
        <DialogContent className="max-w-md" data-testid="restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              Restore from backup
            </DialogTitle>
          </DialogHeader>
          {result ? (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-emerald-900">
                Snapshot from <strong>{(result.snapshot_taken_at || '').slice(0, 19).replace('T', ' ')} UTC</strong> restored.
              </div>
              <div className="text-xs text-muted-foreground">Documents restored per collection:</div>
              <ul className="text-xs space-y-0.5 max-h-48 overflow-y-auto">
                {Object.entries(result.restored || {}).sort().map(([k, v]) => (
                  <li key={k} className="flex justify-between border-b py-0.5">
                    <span className="font-mono">{k}</span><span>{v}</span>
                  </li>
                ))}
              </ul>
              {(result.skipped || []).length > 0 && (
                <div className="text-xs text-amber-700">
                  Skipped (unknown): {result.skipped.join(', ')}
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => { closeRestore(); window.location.reload(); }} data-testid="restore-done-btn">
                  Done — reload page
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-rose-900 text-xs">
                <strong>This will replace ALL current data</strong> — tours, customers, vendors, locations, payments and documents — with the contents of the uploaded backup. Anything created or changed after the backup was taken will be lost. Your login session is preserved.
              </div>

              <div>
                <Label htmlFor="restore-file" className="text-xs">Backup ZIP file</Label>
                <Input
                  id="restore-file"
                  ref={fileRef}
                  type="file"
                  accept=".zip,application/zip"
                  onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                  data-testid="restore-file-input"
                  className="mt-1"
                />
                {restoreFile && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Selected: {restoreFile.name} ({(restoreFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="confirm-text" className="text-xs">
                  Type <span className="font-mono font-bold">RESTORE</span> to confirm
                </Label>
                <Input
                  id="confirm-text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="RESTORE"
                  data-testid="restore-confirm-input"
                  className="mt-1 font-mono"
                />
              </div>

              <DialogFooter className="gap-2">
                <Button variant="ghost" onClick={closeRestore} disabled={restoring}>Cancel</Button>
                <Button
                  onClick={handleRestore}
                  disabled={restoring || !restoreFile || confirmText !== 'RESTORE'}
                  data-testid="confirm-restore-btn"
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                >
                  {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {restoring ? 'Restoring\u2026' : 'Restore now'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SystemBackupCard;
